import { Node, mergeAttributes } from "@tiptap/core";

export type TableVariant = "row-emphasis" | "column-emphasis" | "grid";

/**
 * Minimal read-only Table node for PostViewer.
 * 
 * This is a completely custom Table extension that doesn't inherit from @tiptap/extension-table.
 * The standard Table extension has a NodeView that wraps tables in a div.tableWrapper,
 * which prevents our renderHTML classes from being applied to the actual <table> element.
 * 
 * By defining a minimal Table node from scratch, we ensure:
 * 1. The variant attribute is properly parsed from content
 * 2. renderHTML outputs directly to a <table> element with correct classes
 * 3. No NodeView wrapping interferes with our styling
 */
export const TableViewerNode = Node.create({
  name: "table",
  group: "block",
  content: "tableRow+",
  tableRole: "table",
  isolating: true,

  addAttributes() {
    return {
      variant: {
        default: "row-emphasis",
        parseHTML: (element) => {
          return element.getAttribute("data-table-variant") || "row-emphasis";
        },
        renderHTML: (attributes) => {
          return { "data-table-variant": attributes.variant };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: "table" }];
  },

  renderHTML({ HTMLAttributes }) {
    const variant = HTMLAttributes["data-table-variant"] || HTMLAttributes.variant || "row-emphasis";
    return [
      "table",
      mergeAttributes(
        { class: "border-collapse table-auto w-full my-6" },
        HTMLAttributes,
        {
          "data-table-variant": variant,
          class: `border-collapse table-auto w-full my-6 table-block table-variant-${variant}`,
        }
      ),
      ["tbody", 0],
    ];
  },
});

export default TableViewerNode;
